import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of, shareReplay } from 'rxjs';
import { environment } from '../../environments/environment';
import { Country } from '../models/country';

// Raw shape of GetActiveCountriesRequestHandler's CountryDto as it comes over the wire
// (System.Text.Json camelCases the record's PascalCase property names).
interface CountryApiModel {
  id: number;
  name: string;
  code: string;
  dial: string;
  min: number;
  max: number;
}

function toCountry(dto: CountryApiModel): Country {
  return {
    id: dto.id,
    name: dto.name,
    // DB stores Code uppercase; flag-icons classes (.fi-us) need lower-case.
    iso2: dto.code.toLowerCase(),
    // DB stores Dial with a leading '+' (e.g. '+91'); the input renders its own '+' prefix.
    dialCode: dto.dial.replace(/^\+/, ''),
    min: dto.min,
    max: dto.max
  };
}

// Backs PhoneInputComponent's country/dial-code picker. The endpoint doesn't exist server-side
// yet (DEV-1201) — catchError swallows any failure into an empty list so the phone input still
// renders (just without a country to pick) instead of throwing.
//
// shareReplay(1) on the one shared request means the Business Phone and Mobile Phone instances of
// PhoneInputComponent in the same modal only ever trigger a single GET between them.
@Injectable({
  providedIn: 'root'
})
export class CountriesService {
  private http = inject(HttpClient);

  private countries$: Observable<Country[]> = this.http
    .get<CountryApiModel[]>(`${environment.apiUrl}/SupportContacts/countries`)
    .pipe(
      map(dtos => dtos.map(toCountry)),
      catchError(error => {
        console.error(error);
        return of<Country[]>([]);
      }),
      shareReplay(1)
    );

  getCountries(): Observable<Country[]> {
    return this.countries$;
  }
}
